import {Component, Input, OnDestroy, OnInit, NgZone} from '@angular/core';
import {MatButtonModule, MatFabButton, MatIconButton} from "@angular/material/button";
import {MatIcon, MatIconModule} from "@angular/material/icon";
import {MatMenu, MatMenuItem, MatMenuModule, MatMenuTrigger} from "@angular/material/menu";
import {NgClass, NgForOf, NgIf} from "@angular/common";
import {MidiPlayerService} from "../../service/midi-player.service";
import {BackButtonService} from "../../service/back-button.service";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatSelectModule} from "@angular/material/select";
import {HymnSelectionService} from "../../service/hymn-selection.service";
import {ContinuousPlaybackService} from "../../service/continuous-playback.service";
import {AutoScrollService} from "../../service/auto-scroll.service";
import {SettingsService} from "../../service/settings.service";
import {MatSnackBar, MatSnackBarModule} from "@angular/material/snack-bar";
import {translation} from "../../translation";
//import {CapacitorMusicControls} from "capacitor-music-controls-plugin";

@Component({
  selector: 'app-music-control',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatFormFieldModule,
    MatSelectModule,
    NgForOf,
    NgIf,
    NgClass,
    MatSnackBarModule
  ],
  templateUrl: './music-control.component.html',
  styleUrl: './music-control.component.scss'
})
export class MusicControlComponent implements OnDestroy{

  @Input() mode = 'hymn';

  musicToggle = false;
  private musicBackHandlerActive = false;

  instrumentMap: { [key: string]: { key: string, name: string } } = {
    // add from instruments
    piano: { key: 'piano', name: translation.instrument.piano },
    organ: { key: 'organ', name: translation.instrument.organ },
    violin: { key: 'violin', name: translation.instrument.violin },
    harp: { key: 'harp', name: translation.instrument.harp },
    flute: { key: 'flute', name: translation.instrument.flute },
    guitar: { key: 'guitar', name: translation.instrument.guitar },
    ensemble: { key: 'ensemble', name: translation.instrument.ensemble },
    electric_piano: { key: 'electric_piano', name: translation.instrument.electric_piano },
    synth: { key: 'synth', name: translation.instrument.synth },
    voice: { key: 'voice', name: translation.instrument.voice },
    music_box: { key: 'music_box', name: translation.instrument.music_box },
    random: { key: 'random', name: translation.instrument.random },
  };

  instruments: { key: string, name: string }[] = [];

  instrument1: { key: string, name: string } = this.instrumentMap['random'];
  instrument2: { key: string, name: string } | null = this.instrumentMap['harp'];
  constructor(protected midiPlayerService: MidiPlayerService,
              protected hymnSelectionService: HymnSelectionService,
              protected continuousPlaybackService: ContinuousPlaybackService,
              protected autoScrollService: AutoScrollService,
              protected settingsService: SettingsService,
              protected snackbar: MatSnackBar,
              private backButtonService: BackButtonService,
              private zone: NgZone) {

    const initFromSettings = () => {
      // Build instrument options from enabled list (exclude 'random')
      const enabled = this.settingsService.getEnabledInstruments();
      // Build unique list, excluding 'random' regardless of settings
      const seen = new Set<string>();
      this.instruments = enabled
        .filter(k => k !== 'random')
        .filter(k => {
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        })
        .map(k => this.instrumentMap[k])
        .filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name));

      // Apply selected instruments
      const current = this.settingsService.getInstruments();
      if (current.length > 0) {
        this.instrument1 = this.instrumentMap[current[0]] || this.instrumentMap['random'];
        if (current.length > 1) {
          this.instrument2 = this.instrumentMap[current[1]] || null;
        } else {
          this.instrument2 = null;
        }
      }

      if (this.settingsService.getAutoscroll()) {
        this.autoScrollService.enableAutoScroll();
      }
    };

    this.settingsService.loadSettings().then(() => {
      initFromSettings();

      // react to enabled instruments changes: refresh list and selection
      this.settingsService.enabledInstruments$.subscribe(() => {
        initFromSettings();
      });

      // react to instrument selections (e.g., when disabled forces change)
      this.settingsService.instruments$.subscribe((vals) => {
        if (vals && vals.length > 0) {
          this.instrument1 = this.instrumentMap[vals[0]] || this.instrumentMap['random'];
          this.instrument2 = vals.length > 1 ? (this.instrumentMap[vals[1]] || null) : null;
        } else {
          this.instrument1 = this.instrumentMap['random'];
          this.instrument2 = null;
        }
        // ensure the midi player reflects any programmatic changes
        this.midiPlayerService.changeInstrument(this.getInstruments());
      });
    });

    window.addEventListener('click', (e) =>{
      if (this.musicToggle && !document.getElementById('music-menu-container')?.contains(e.target as Node) && !document.getElementsByClassName('cdk-overlay-container')[0]?.contains(e.target as Node)) {
        this.musicToggle = false;
        this.musicBackHandlerActive = false;
      }
    });

    midiPlayerService.progress$.subscribe((progress) => {
      let percentageRounded = midiPlayerService.introPlaying? 100 - Math.round(progress) : Math.round(progress);
      let invertedPercentage = 100 - percentageRounded;

      document.documentElement.style.setProperty('--circle-percentage', `${percentageRounded}%`);
      document.documentElement.style.setProperty('--circle-percentage-invert', `${invertedPercentage}%`);
    });
  }

  ngOnDestroy(): void {
    // try {
    //   CapacitorMusicControls.destroy();
    // } catch (e) {
    //   console.log(e);
    // }
  }

  async changeSpeed($event: any, increase: boolean) {
    $event.stopPropagation();
    $event.preventDefault();
    if(increase) {
      this.midiPlayerService.increasePlaybackSpeed();
    } else {
      this.midiPlayerService.decreasePlaybackSpeed();
    }
  }

  async clickInstrument($event: MouseEvent, instrument: string, instrument1: boolean) {
    $event.stopPropagation();
    $event.preventDefault();

    const resolve = (key: string) => {
      if (key === 'random') return this.instrumentMap['random'];
      return this.instruments.find(i => i.key === key) || this.instrumentMap[key];
    };

    if (instrument1) {
      this.instrument1 = resolve(instrument)!;
      if (this.instrument2?.key === instrument) {
        this.instrument2 = null;
      }
    } else {
      this.instrument2 = resolve(instrument)!;
    }

    this.midiPlayerService.changeInstrument(this.getInstruments());
    await this.settingsService.setInstruments(this.getInstruments());
  }

  // --- Transpose handlers ---
  async changeTranspose($event: MouseEvent, delta: number) {
    $event.stopPropagation();
    $event.preventDefault();
    await this.midiPlayerService.incrementTranspose(delta);
  }

  async resetTranspose($event: MouseEvent) {
    $event.stopPropagation();
    $event.preventDefault();
    await this.midiPlayerService.resetTranspose();
  }

  formatTranspose(n: number): string {
    if (n === 0) return '0';
    return n > 0 ? `+${n}` : `${n}`;
  }

  async clearInstrument2($event: MouseEvent) {
    $event.stopPropagation();
    $event.preventDefault();
    this.instrument2 = null;
    this.midiPlayerService.changeInstrument(this.getInstruments());
    await this.settingsService.setInstruments(this.getInstruments());
  }

  onInstrument1Change(key: string) {
    if (key === 'random') {
      this.instrument1 = this.instrumentMap['random'];
    } else {
      this.instrument1 = this.instruments.find(i => i.key === key) || this.instrumentMap[key]!;
    }
    if (this.instrument2?.key === key) {
      this.instrument2 = null;
    }
    this.midiPlayerService.changeInstrument(this.getInstruments());
    this.settingsService.setInstruments(this.getInstruments());
  }

  onInstrument2Change(key: string | null) {
    if (!key) {
      this.instrument2 = null;
    } else {
      this.instrument2 = this.instruments.find(i => i.key === key)!;
    }
    this.midiPlayerService.changeInstrument(this.getInstruments());
    this.settingsService.setInstruments(this.getInstruments());
  }

  getInstruments() {
    const instruments = [this.instrument1.key];
    if(this.instrument2) {
      instruments.push(this.instrument2.key);
    }
    return instruments;
  }

  playMidi() {
    if(this.autoScrollService.enabled) {
      this.autoScrollService.startAutoScroll();
    }
    this.midiPlayerService.playMidi(this.hymnSelectionService.selectedHymn, this.settingsService.getInstruments(), this.hymnSelectionService.selectedHymn.numberOfVerses, this.midiPlayerService.playbackSpeed, this.settingsService.getIntroEnabled())
  }

  disableAutoScroll() {
    this.settingsService.setAutoscroll(false);
    this.autoScrollService.disableAutoScroll();
    this.snackbar.open(translation.autoscroll_disabled, '✕', { duration: 2000 });
  }

  startAutoScroll() {
    this.settingsService.setAutoscroll(true);
    this.autoScrollService.startAutoScroll();
    this.snackbar.open(translation.autoscroll_enabled, '✕', { duration: 2000 });
  }

  toggleMusicMenu() {
    if (!this.musicToggle) {
      this.musicToggle = true;
      if (!this.musicBackHandlerActive) {
        this.musicBackHandlerActive = true;
        this.backButtonService.registerBackButtonAction(() => {
          if (this.musicToggle) {
            this.zone.run(() => {
              this.musicToggle = false;
              this.musicBackHandlerActive = false;
            });
          }
        });
      }
    } else {
      this.musicToggle = false;
      this.musicBackHandlerActive = false;
    }
  }

  protected readonly translation = translation;
}
